import { type GeckoRiskAssessmentConfig, RiskMatrixOrientation } from 'types'

/**
 * Determines if likelihood is represented as rows in the risk matrix
 */
export function getIsLikelihoodAsRows(
  riskMatrixOrientation: RiskMatrixOrientation | undefined
): boolean {
  return (
    (riskMatrixOrientation ?? RiskMatrixOrientation.LIKELIHOOD_AS_ROWS) ===
    RiskMatrixOrientation.LIKELIHOOD_AS_ROWS
  )
}

/**
 * Calculates the matrix position (row, col) from likelihood and consequence values
 */
export function calculateMatrixPosition(
  likelihood: number,
  consequence: number,
  isLikelihoodAsRows: boolean
): { row: number; col: number } {
  return {
    row: isLikelihoodAsRows ? likelihood - 1 : consequence - 1,
    col: isLikelihoodAsRows ? consequence - 1 : likelihood - 1,
  }
}

/**
 * Calculates likelihood and consequence values from a matrix position
 */
export function calculateLikelihoodConsequence(
  position: { row: number; col: number },
  isLikelihoodAsRows: boolean
): { likelihood: number; consequence: number } {
  return {
    likelihood: isLikelihoodAsRows ? position.row + 1 : position.col + 1,
    consequence: isLikelihoodAsRows ? position.col + 1 : position.row + 1,
  }
}

/**
 * Looks up the risk class for a likelihood/consequence pair in the risk matrix.
 * Returns null when either axis is unset (<= 0) or out of the matrix bounds.
 */
export function lookupRiskClass(
  likelihood: number,
  consequence: number,
  config?: GeckoRiskAssessmentConfig
): string | null {
  if (likelihood <= 0 || consequence <= 0) return null

  const riskMatrix = config?.data.riskMatrix
  if (!riskMatrix || riskMatrix.length === 0) return null

  const isLikelihoodRows = getIsLikelihoodAsRows(
    config?.data.riskMatrixOrientation
  )
  const { row, col } = calculateMatrixPosition(
    likelihood,
    consequence,
    isLikelihoodRows
  )

  if (
    row >= 0 &&
    row < riskMatrix.length &&
    col >= 0 &&
    col < riskMatrix[row].length
  ) {
    return riskMatrix[row][col]
  }

  return null
}
